/**
 * Migration 0095 — request-and-approve booking mode, staff-side API client.
 *
 * Deliberately a separate module from `@/lib/api` (raw `fetch`, same
 * `credentials: 'include'` contract): the Today board + AppShell tests mock
 * `@/lib/api` with a fixed function set, so a new export there would break
 * them. Every helper here fails soft — callers treat a thrown error as
 * "nothing to show" so a missing capability (403) or a test environment
 * without `fetch` never crashes the page.
 */
import type { BookingRequestRow, BookingRequestSettings } from './types';

export class BookingRequestsApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
  ) {
    super(`${status} ${code}`);
    this.name = 'BookingRequestsApiError';
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown };
    const code = typeof body.error === 'string' ? body.error : `http_${res.status}`;
    throw new BookingRequestsApiError(code, res.status);
  }
  return (await res.json()) as T;
}

export interface ListBookingRequestsResponse {
  expiryHours: number;
  requests: BookingRequestRow[];
}

export const listBookingRequests = (facilityId?: string | null) =>
  call<ListBookingRequestsResponse>(
    `/bookings/requests${facilityId ? `?facilityId=${encodeURIComponent(facilityId)}` : ''}`,
  );

export const countBookingRequests = (facilityId?: string | null) =>
  call<{ count: number }>(
    `/bookings/requests/count${facilityId ? `?facilityId=${encodeURIComponent(facilityId)}` : ''}`,
  );

export const approveBookingRequest = (bookingId: string) =>
  call<{ ok: true; status: 'confirmed' | 'pending_payment'; bookingIds: string[] }>(
    `/bookings/${bookingId}/approve`,
    { method: 'POST' },
  );

export const declineBookingRequest = (bookingId: string, reason?: string) =>
  call<{ ok: true; status: 'cancelled'; bookingIds: string[] }>(`/bookings/${bookingId}/decline`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });

export const getBookingRequestSettings = () =>
  call<{ settings: BookingRequestSettings }>('/me/booking-request-settings');

export const updateBookingRequestSettings = (patch: Partial<BookingRequestSettings>) =>
  call<{ settings: BookingRequestSettings }>('/me/booking-request-settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
